import api from "./api";

const SERVICE_BASE = "/service";
export const SERVICE_EXPORT_FALLBACK_FILENAME = "service_export.xlsx";

function ticketPath(ticketId, suffix = "") {
  return `${SERVICE_BASE}/tickets/${encodeURIComponent(ticketId)}${suffix}`;
}

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function normalizeServiceTicketResponse(response) {
  return isObject(response?.data) ? response.data : null;
}

export function normalizeServiceSelectorResponse(response) {
  const data = isObject(response?.data) ? response.data : {};
  return {
    hold_side: Array.isArray(data.hold_side) ? data.hold_side : [],
    used_side: Array.isArray(data.used_side) ? data.used_side : [],
  };
}

export function normalizeServiceCommandResponse(response) {
  const data = response?.data;
  if (
    !isObject(data) ||
    !isObject(data.ticket) ||
    !Array.isArray(data.hold_side) ||
    !Array.isArray(data.used_side)
  ) {
    return null;
  }
  return {
    ...data,
    message: typeof response?.message === "string" ? response.message : "",
    ticket: data.ticket,
    hold_side: data.hold_side,
    used_side: data.used_side,
  };
}

export async function listServiceTickets(params) {
  const config = Array.isArray(params?.stages)
    ? { params, paramsSerializer: { indexes: null } }
    : { params };
  const { data } = await api.get(`${SERVICE_BASE}/tickets`, config);
  return data;
}

const CANNIBAL_BASE = `${SERVICE_BASE}/cannibal-devices`;
export const CANNIBAL_EXPORT_FALLBACK_FILENAME = "cannibal_devices.xlsx";
export const CANNIBAL_LIST_QUERY_PARAMETERS = Object.freeze([
  "page",
  "page_size",
  "search",
  "branch",
  "status",
  "cannibal_id",
  "serial_number",
  "model",
  "stock_reference_status",
  "removed_parts",
  "created_date",
  "updated_date",
  "created_date_from",
  "created_date_to",
]);

export function normalizeCannibalListParams(params = {}) {
  return Object.fromEntries(CANNIBAL_LIST_QUERY_PARAMETERS.flatMap((key) => {
    const value = params?.[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed ? [[key, trimmed]] : [];
    }
    return value == null ? [] : [[key, value]];
  }));
}

export async function listCannibalDevices(params = {}) {
  const { data } = await api.get(CANNIBAL_BASE, { params: normalizeCannibalListParams(params) });
  return data;
}

export async function getCannibalDevice(donorId) {
  const { data } = await api.get(`${CANNIBAL_BASE}/${encodeURIComponent(donorId)}`);
  return data;
}

export async function createCannibalDevice(payload) {
  const { data } = await api.post(CANNIBAL_BASE, payload);
  return data;
}

export async function updateCannibalDevice(donorId, payload) {
  const { data } = await api.patch(`${CANNIBAL_BASE}/${encodeURIComponent(donorId)}`, payload);
  return data;
}

export async function getCannibalDeviceHistory(donorId) {
  const { data } = await api.get(`${CANNIBAL_BASE}/${encodeURIComponent(donorId)}/history`);
  return data;
}

export async function getCannibalTransferPrefill(donorId) {
  const { data } = await api.get(`${CANNIBAL_BASE}/${encodeURIComponent(donorId)}/transfer-prefill`);
  return data;
}

export async function listItemMaster(params = {}) {
  const { data } = await api.get("/items", { params });
  return Array.isArray(data) ? data : [];
}

export async function exportCannibalDevices() {
  try {
    const response = await api.post(`${CANNIBAL_BASE}/export`, {}, { responseType: "blob" });
    return {
      blob: response.data,
      filename: getServiceExportFilename(response.headers?.["content-disposition"]) === SERVICE_EXPORT_FALLBACK_FILENAME
        ? CANNIBAL_EXPORT_FALLBACK_FILENAME
        : getServiceExportFilename(response.headers?.["content-disposition"]),
    };
  } catch (error) {
    throw await normalizeServiceBlobError(error);
  }
}

export const downloadCannibalExport = downloadServiceExport;

const SERVICE_AUDIT_QUERY_PARAMETERS = [
  "page",
  "page_size",
  "action",
  "entity_type",
  "ticket_number",
  "actor",
  "date_from",
  "date_to",
  "search",
];
const SERVICE_AUDIT_BRANCHES = new Set(["jakarta", "surabaya", "bali"]);

export function buildServiceAuditQuery(params = {}) {
  return SERVICE_AUDIT_QUERY_PARAMETERS.reduce((query, key) => {
    const value = params[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) query[key] = trimmed;
    } else if (value !== null && value !== undefined && value !== "") {
      query[key] = value;
    }
    return query;
  }, SERVICE_AUDIT_BRANCHES.has(params.branch) ? { branch: params.branch } : {});
}

export async function listServiceAuditLogs(params = {}) {
  const { data } = await api.get(`${SERVICE_BASE}/audit-logs`, {
    params: buildServiceAuditQuery(params),
  });
  return data;
}

export async function createServiceTicket(payload) {
  const { data } = await api.post(`${SERVICE_BASE}/tickets`, payload);
  return data;
}

export async function getServiceTicket(ticketId) {
  const { data } = await api.get(ticketPath(ticketId));
  return data;
}

export async function getServiceTicketSqAction(ticketId, { signal } = {}) {
  const { data } = await api.get(
    `/quotations/service-tickets/${encodeURIComponent(ticketId)}/sq`,
    { signal }
  );
  return isObject(data?.data) ? data.data : null;
}

export async function updateServiceTicket(ticketId, payload) {
  const { data } = await api.patch(ticketPath(ticketId), payload);
  return data;
}

export async function moveServiceTicket(ticketId, payload) {
  const { data } = await api.post(ticketPath(ticketId, "/move"), payload);
  return data;
}

export async function getServiceTicketStageHistory(ticketId, params) {
  const { data } = await api.get(ticketPath(ticketId, "/stage-history"), { params });
  return data;
}

export async function getServiceSparePartOptions(ticketId) {
  const { data } = await api.get(ticketPath(ticketId, "/spare-part-options"));
  return data;
}

export async function listServiceTechnicians(params) {
  const { data } = await api.get(`${SERVICE_BASE}/technicians`, { params });
  return data;
}

export async function listActiveServiceTechnicians() {
  const technicians = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await listServiceTechnicians({
      is_active: true,
      page,
      page_size: 100,
    });
    const data = Array.isArray(response?.data) ? response.data : [];
    technicians.push(...data.filter((technician) => technician?.is_active === true));
    totalPages = Number.isInteger(response?.pagination?.total_pages)
      ? response.pagination.total_pages
      : page;
    page += 1;
  } while (page <= totalPages);

  return technicians;
}

export async function listServiceDeviceModels(params) {
  const { data } = await api.get(`${SERVICE_BASE}/device-models`, { params });
  return data;
}

export async function listActiveServiceDeviceModels() {
  const models = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await listServiceDeviceModels({
      is_active: true,
      page,
      page_size: 100,
    });
    const data = Array.isArray(response?.data) ? response.data : [];
    models.push(...data.filter((model) => model?.is_active === true));
    totalPages = Number.isInteger(response?.pagination?.total_pages)
      ? response.pagination.total_pages
      : page;
    page += 1;
  } while (page <= totalPages);

  return models;
}

export async function softDeleteServiceTicket(ticketId, payload) {
  const { data } = await api.delete(ticketPath(ticketId), { data: payload });
  return data;
}

export async function restoreServiceTicket(ticketId, payload) {
  const { data } = await api.post(ticketPath(ticketId, "/restore"), payload);
  return data;
}

export async function checkServiceSerialNumber(payload) {
  const { data } = await api.post(`${SERVICE_BASE}/sn-check`, payload);
  return data;
}

function safeExportFilename(value) {
  if (typeof value !== "string") return null;
  const filename = value.trim();
  if (
    !filename ||
    filename === "." ||
    filename === ".." ||
    filename.includes("/") ||
    filename.includes("\\") ||
    /[\u0000-\u001f\u007f]/.test(filename) ||
    !filename.toLowerCase().endsWith(".xlsx")
  ) {
    return null;
  }
  return filename;
}

export function getServiceExportFilename(contentDisposition) {
  if (typeof contentDisposition !== "string") return SERVICE_EXPORT_FALLBACK_FILENAME;

  const encodedMatch = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (encodedMatch) {
    try {
      const decoded = decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ""));
      const safeDecoded = safeExportFilename(decoded);
      if (safeDecoded) return safeDecoded;
    } catch {
      // Fall through to the regular filename or safe fallback.
    }
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);
  const filename = safeExportFilename(filenameMatch?.[1] || filenameMatch?.[2]);
  return filename || SERVICE_EXPORT_FALLBACK_FILENAME;
}

export async function normalizeServiceBlobError(error) {
  const data = error?.response?.data;
  if (!(typeof Blob !== "undefined" && data instanceof Blob)) return error;

  try {
    const text = await data.text();
    const parsed = JSON.parse(text);
    error.response = { ...error.response, data: parsed };
  } catch {
    // Preserve the original error when a Blob is not a JSON Service error.
  }
  return error;
}

export async function exportServiceTickets(payload) {
  try {
    const response = await api.post(`${SERVICE_BASE}/exports/tickets`, payload, {
      responseType: "blob",
    });
    return {
      blob: response.data,
      filename: getServiceExportFilename(response.headers?.["content-disposition"]),
      contentType: response.headers?.["content-type"] || response.data?.type || "",
    };
  } catch (error) {
    throw await normalizeServiceBlobError(error);
  }
}

export function downloadServiceExport(
  { blob, filename },
  { documentRef = document, urlApi = URL } = {}
) {
  const objectUrl = urlApi.createObjectURL(blob);
  let anchor;
  try {
    anchor = documentRef.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename || SERVICE_EXPORT_FALLBACK_FILENAME;
    anchor.style.display = "none";
    documentRef.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor?.remove();
    urlApi.revokeObjectURL(objectUrl);
  }
}
